import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const LandingContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(-45deg, #667eea, #764ba2, #f093fb, #f5576c);
  background-size: 400% 400%;
  animation: gradientShift 15s ease infinite;
  padding: 2rem;
  position: relative;
  overflow: hidden;

  @keyframes gradientShift {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.1);
    backdrop-filter: blur(1px);
  }
`;

const LandingCard = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 24px;
  padding: 3rem;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  text-align: center;
  max-width: 600px;
  width: 100%;
  position: relative;
  z-index: 10;
  animation: slideInFromBottom 0.6s ease-out;

  @keyframes slideInFromBottom {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const Logo = styled.div`
  margin-bottom: 2rem;

  .logo-icon {
    font-size: 4rem;
    margin-bottom: 1rem;
    display: block;
  }

  .logo-text {
    font-family: 'Poppins', sans-serif;
    font-size: 2.5rem;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 0.5rem;
  }

  .logo-subtitle {
    color: #64748b;
    font-size: 1.1rem;
    font-weight: 500;
  }
`;

const WelcomeText = styled.div`
  margin-bottom: 3rem;

  h1 {
    font-size: 1.5rem;
    color: #1e293b;
    margin-bottom: 1rem;
    font-weight: 600;
  }

  p {
    color: #64748b;
    font-size: 1rem;
    line-height: 1.6;
  }
`;

const PortalOptions = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const PortalCard = styled.div`
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 16px;
  padding: 2rem 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    border-color: #2563eb;
    transform: translateY(-4px);
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  }

  &:active {
    transform: translateY(-2px);
  }

  .portal-icon {
    font-size: 3rem;
    margin-bottom: 1rem;
    display: block;
  }

  .portal-title {
    font-size: 1.25rem;
    font-weight: 600;
    color: #1e293b;
    margin-bottom: 0.5rem;
  }

  .portal-description {
    color: #64748b;
    font-size: 0.9rem;
    line-height: 1.5;
  }
`;

const FooterText = styled.div`
  color: #64748b;
  font-size: 0.9rem;
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: center;
`;

const FooterLink = styled(Link)`
  color: #2563eb;
  font-weight: 600;
`;

function LandingPage() {
  const navigate = useNavigate();

  const handlePortalSelect = (portal) => {
    navigate(portal === 'admin' ? '/admin-login' : '/presenter-login');
  };

  return (
    <LandingContainer>
      <LandingCard>
        <Logo>
          <span className="logo-icon">⚡</span>
          <div className="logo-text">BidMaster Pro</div>
          <div className="logo-subtitle">Professional Bidding Platform</div>
        </Logo>

        <WelcomeText>
          <h1>Welcome to BidMaster Pro</h1>
          <p>
            Choose your portal to access the bidding management system. 
            Admins can manage items, shows, and presenters, while presenters 
            can view live bids and manage auctions.
          </p>
        </WelcomeText>

        <PortalOptions>
          <PortalCard onClick={() => handlePortalSelect('admin')}>
            <span className="portal-icon">👨‍💼</span>
            <div className="portal-title">Admin Portal</div>
            <div className="portal-description">
              Manage items, radio shows, and presenters with full administrative control
            </div>
          </PortalCard>

          <PortalCard onClick={() => handlePortalSelect('presenter')}>
            <span className="portal-icon">🎙️</span>
            <div className="portal-title">Presenter Portal</div>
            <div className="portal-description">
              View live bids and manage auctions with presenter tools.
            </div>
          </PortalCard>
        </PortalOptions>

        <FooterText>
          <p>Select a portal to continue to the login page</p>
          <FooterLink to="/terms">View Terms and Conditions</FooterLink>
        </FooterText>
      </LandingCard>
    </LandingContainer>
  );
}

export default LandingPage; 
